import { useState } from "react";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const card = "rounded-2xl bg-white border border-[#0B2A4A]/10 p-5 sm:p-6 print:border-0 print:p-0 print:break-inside-avoid";
const inputCls = "bg-[#FAF6EE]/60 font-normal placeholder:text-slate-400 placeholder:italic";
const col = (c) => (typeof c === "string" ? { label: c } : c);

function Head({ b }) {
  if (!b.title && !b.help) return null;
  return (
    <div className="mb-4">
      {b.title && <h3 className="font-semibold text-[#0B2A4A] text-lg">{b.title}</h3>}
      {b.help && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{b.help}</p>}
    </div>
  );
}

function Choice({ id, options, optionHelp, value, set }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map((o, i) => (
        <button type="button" key={o} onClick={() => set(id, value === o ? "" : o)} aria-pressed={value === o}
          className={`rounded-xl border px-3.5 py-2 text-left text-sm transition-colors ${value === o ? "bg-[#0B2A4A] border-[#0B2A4A] text-[#F5ECD7]" : "bg-[#FAF6EE]/60 border-[#0B2A4A]/15 text-[#0B2A4A] hover:border-[#1A9AA8]"}`}
          data-testid={`ws-${id}-opt-${i}`}>
          <span className="font-semibold">{o}</span>{optionHelp?.[i] && <span className={`block text-xs ${value === o ? "text-[#F5ECD7]/80" : "text-slate-500"}`}>{optionHelp[i]}</span>}
        </button>
      ))}
    </div>
  );
}

export function Field({ fl, value, set }) {
  let control;
  if (fl.options) control = <Choice id={fl.id} options={fl.options} optionHelp={fl.option_help} value={value} set={set} />;
  else if (fl.kind === "long") control = <Textarea rows={3} placeholder={fl.ph || ""} value={value || ""} onChange={(e) => set(fl.id, e.target.value)} className={inputCls} data-testid={`ws-${fl.id}`} />;
  else control = <Input type={fl.kind === "number" ? "number" : "text"} inputMode={fl.kind === "number" ? "decimal" : undefined} placeholder={fl.ph || ""} value={value || ""} onChange={(e) => set(fl.id, e.target.value)} className={`${inputCls} ${fl.kind === "number" ? "max-w-xs" : ""}`} data-testid={`ws-${fl.id}`} />;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm font-semibold text-[#0B2A4A]">{fl.prompt}</p>
      {fl.help && <p className="text-xs text-slate-500 -mt-0.5">{fl.help}</p>}
      {control}
    </div>
  );
}

function FieldsBlock({ b, a, set }) {
  return (
    <div className={card}>
      <Head b={b} />
      <div className="flex flex-col gap-5">{b.fields.map((fl) => <Field key={fl.id} fl={fl} value={a[fl.id]} set={set} />)}</div>
    </div>
  );
}

function ChecklistBlock({ b, a, set }) {
  return (
    <div className={card}>
      <h3 className="font-semibold text-[#0B2A4A]">{b.title}</h3>
      <p className="mt-1 text-xs text-slate-500 no-print">Tick each one when it's done.</p>
      <ul className="mt-3 flex flex-col gap-2">
        {b.items.map((it, i) => {
          const k = `${b.id}_${i}`;
          return (
            <li key={k}><label className="flex gap-3 items-start text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={a[k] === "1"} onChange={(e) => set(k, e.target.checked ? "1" : "")} className="mt-0.5 h-4 w-4 accent-[#F08A3C]" data-testid={`ws-${k}`} />{it}
            </label></li>
          );
        })}
      </ul>
    </div>
  );
}

function useRows(b, a) {
  const filled = Array.from({ length: b.rows }, (_, r) => b.columns.some((_, c) => a[`${b.id}_${r}_${c}`])).lastIndexOf(true) + 1;
  const [revealed, setRevealed] = useState(0);
  const shown = Math.min(b.rows, Math.max(b.start || b.rows, filled, revealed));
  return [shown, () => setRevealed(shown + 1)];
}

function AddRow({ b, shown, add, noun }) {
  if (shown >= b.rows) return null;
  return <button type="button" onClick={add} className="no-print self-start inline-flex items-center gap-1.5 rounded-xl border border-dashed border-[#1A9AA8] text-[#147C88] px-4 py-2 text-sm font-semibold" data-testid={`ws-${b.id}-add-row`}><Plus className="h-4 w-4" /> Add another {noun}</button>;
}

function TableBlock({ b, a, set }) {
  const labels = b.row_labels || [];
  const cols = b.columns.map(col);
  const [shown, add] = useRows(b, a);
  const noun = b.item || "row";
  return (
    <div className={card} data-testid={`ws-table-${b.id}`}>
      <Head b={b} />
      <div className="flex flex-col gap-3">
        {Array.from({ length: shown }, (_, r) => (
          <div key={r} className="rounded-xl border border-[#0B2A4A]/10 bg-[#FAF6EE]/40 p-4 print:break-inside-avoid" data-testid={`ws-${b.id}-row-${r}`}>
            <p className="text-xs font-bold uppercase tracking-wider text-[#147C88] mb-3">{labels[r] || `${noun} ${r + 1}`}</p>
            <div className={`grid gap-3 ${cols.length > 1 ? "sm:grid-cols-2" : ""}`}>
              {cols.map((c, ci) => {
                const k = `${b.id}_${r}_${ci}`;
                return (
                  <div key={c.label} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-[#0B2A4A]">{c.label}</span>
                    {c.options ? <Choice id={k} options={c.options} value={a[k]} set={set} />
                      : <Textarea rows={1} placeholder={c.ph || ""} value={a[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${inputCls} min-h-[40px] bg-white`} data-testid={`ws-${k}`} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <AddRow b={b} shown={shown} add={add} noun={noun.toLowerCase()} />
      </div>
    </div>
  );
}

function ScoresBlock({ b, a, set }) {
  const [shown, add] = useRows(b, a);
  const crit = b.columns.slice(1).map(col);
  return (
    <div className={card} data-testid={`ws-scores-${b.id}`}>
      <Head b={b} />
      <ul className="mb-4 grid sm:grid-cols-3 gap-2 text-xs text-slate-600">{crit.map((c) => <li key={c.label} className="rounded-lg bg-[#F5ECD7]/60 px-3 py-2"><span className="font-semibold text-[#0B2A4A]">{c.label}:</span> {c.ph}</li>)}</ul>
      <div className="flex flex-col gap-3">
        {Array.from({ length: shown }, (_, r) => {
          const total = crit.reduce((s, _, ci) => s + (Number(a[`${b.id}_${r}_${ci + 1}`]) || 0), 0);
          return (
            <div key={r} className="rounded-xl border border-[#0B2A4A]/10 bg-[#FAF6EE]/40 p-4 print:break-inside-avoid" data-testid={`ws-${b.id}-row-${r}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <Input placeholder={`${col(b.columns[0]).label} ${r + 1}${col(b.columns[0]).ph ? ` · ${col(b.columns[0]).ph}` : ""}`} value={a[`${b.id}_${r}_0`] || ""} onChange={(e) => set(`${b.id}_${r}_0`, e.target.value)} className={`${inputCls} bg-white`} data-testid={`ws-${b.id}_${r}_0`} />
                <span className="shrink-0 rounded-lg bg-[#0B2A4A] text-[#F5ECD7] px-3 py-2 text-sm font-bold" data-testid={`ws-${b.id}-total-${r}`}>{total} / {crit.length * 5}</span>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {crit.map((c, ci) => (
                  <div key={c.label} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-[#0B2A4A]">{c.label}</span>
                    <Choice id={`${b.id}_${r}_${ci + 1}`} options={["1", "2", "3", "4", "5"]} value={a[`${b.id}_${r}_${ci + 1}`]} set={set} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <AddRow b={b} shown={shown} add={add} noun={col(b.columns[0]).label.toLowerCase()} />
      </div>
    </div>
  );
}

const RC_IN = [["freedom", "My Freedom Number (monthly USD)", "2000"], ["hours", "Hours a week I can give to client work", "10"], ["fee", "Platform fee % (Fiverr ~20 · Upwork 0–15 · direct 0)", "15"],
  ["basic", "Hours to deliver Basic", "2"], ["standard", "Hours to deliver Standard", "5"], ["premium", "Hours to deliver Premium", "8"], ["avg", "Average project price (USD, blank = Standard)", ""]];

function RateCalcBlock({ b, a, set }) {
  const v = (k, d) => Number(a[`${b.id}_${k}`] || d) || 0;
  const keep = v("hours", 10) ? v("freedom", 2000) / (v("hours", 10) * 4.33) : 0;
  const charge = keep / (1 - v("fee", 15) / 100);
  const first = charge * 0.5;
  const pkg = (k, d) => Math.round(v(k, d) * charge);
  const avg = v("avg", 0) || pkg("standard", 5);
  const projects = avg ? Math.ceil(v("freedom", 2000) / (avg * (1 - v("fee", 15) / 100))) : 0;
  const out = [["Target hourly rate (what I keep)", `$${keep.toFixed(2)}`], ["Target hourly rate (what I charge incl. fee)", `$${charge.toFixed(2)}`], ["First 3 clients rate (about half)", `$${first.toFixed(2)}`],
    ["Basic price", `$${pkg("basic", 2)} (first clients $${Math.round(v("basic", 2) * first)})`], ["Standard price", `$${pkg("standard", 5)} (first clients $${Math.round(v("standard", 5) * first)})`],
    ["Premium price", `$${pkg("premium", 8)} (first clients $${Math.round(v("premium", 8) * first)})`], ["Projects per month to reach my Freedom Number", String(projects)]];
  return (
    <div className={`${card} grid md:grid-cols-2 gap-6`} data-testid="ws-ratecalc">
      <div className="flex flex-col gap-3">{RC_IN.map(([k, l, ph]) => <label key={k} className="flex flex-col gap-1 text-sm font-semibold text-[#0B2A4A]">{l}<Input type="number" min="0" placeholder={ph} value={a[`${b.id}_${k}`] || ""} onChange={(e) => set(`${b.id}_${k}`, e.target.value)} className={inputCls} data-testid={`ws-${b.id}_${k}`} /></label>)}</div>
      <dl className="rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] p-5 flex flex-col gap-3 self-start">{out.map(([l, val], i) => <div key={l} className="flex justify-between gap-3 text-sm"><dt>{l}</dt><dd className="font-bold text-[#F08A3C]" data-testid={`ws-rc-out-${i}`}>{val}</dd></div>)}</dl>
    </div>
  );
}

const BLOCKS = { fields: FieldsBlock, checklist: ChecklistBlock, table: TableBlock, scores: ScoresBlock, ratecalc: RateCalcBlock };

export function WorksheetBlocks({ blocks, a, set }) {
  return blocks.map((b, i) => { const B = BLOCKS[b.type]; return <B key={b.id || i} b={b} a={a} set={set} />; });
}
